import React from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { store, persistor } from './store'
import { AuthInitializer } from './store/modules/auth/AuthInitializer'
import { CartInitializer } from './store/modules/cart/CartInitializer'
import { ScrollToTop, ProtectedRoute, CheckoutRoute, Toast } from './shared/components'
import { useCartToast } from './hooks/useCartToast'
import { useCart } from './store/modules/cart'
import { ScrapPreferenceProvider } from './contexts/ScrapPreferenceContext'

// Importar páginas
import Home from './pages/Home'
import About from './pages/About'
import Batteries from './pages/Batteries'
import Brands from './pages/Brands'
import TermsOfUse from './pages/TermsOfUse'
import PrivacyPolicy from './pages/PrivacyPolicy'
import FipeTest from './pages/FipeTest'
import NotFound from './pages/NotFound'
import HelpCenter from './pages/HelpCenter'

// Módulos - Importar páginas
import { Login, Register, ForgotPassword, ResetPassword } from './modules/auth/pages'
import { Account } from './modules/account/pages'
import { Orders } from './modules/orders/pages'
import { Checkout } from './modules/checkout/pages'
import { CatalogSelection, BatteryCatalog, BatteryDetails } from './modules/catalog/pages'
import {
  AdminDashboard,
  AdminProducts,
  AdminOrders,
  AdminReports,
  AdminCoupons,
  AdminCustomers,
  AdminReviews,
  AdminBrands,
  AdminCategories,
  AdminShippingZones,
  AdminEmailTemplates
} from './modules/admin/pages'

const ScrapPreferenceWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { openCart } = useCart()
  return (
    <ScrapPreferenceProvider openCart={openCart}>
      {children}
    </ScrapPreferenceProvider>
  )
}

const AppContent: React.FC = () => {
  useCartToast()

  return (
    <>
      <AuthInitializer />
      <CartInitializer />
      <ScrapPreferenceWrapper>
      <Router>
        <ScrollToTop />
        <Toast />
      <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/sobre" element={<About />} />
            <Route path="/baterias" element={<Batteries />} />
            <Route path="/marcas" element={<Brands />} />
            <Route path="/catalogo" element={<CatalogSelection />} />
            <Route path="/catalogo/:CATEGORIA" element={<BatteryCatalog />} />
            <Route path="/produto/:slug" element={<BatteryDetails />} />
            <Route path="/checkout" element={
              <CheckoutRoute>
                <Checkout />
              </CheckoutRoute>
            } />
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<Register />} />
            <Route path="/esqueci-senha" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/conta" element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            } />
            <Route path="/pedidos" element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/produtos" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminProducts />
              </ProtectedRoute>
            } />
            <Route path="/admin/pedidos" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminOrders />
              </ProtectedRoute>
            } />
            <Route path="/admin/relatorios" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminReports />
              </ProtectedRoute>
            } />
            <Route path="/admin/cupons" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminCoupons />
              </ProtectedRoute>
            } />
            <Route path="/admin/clientes" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminCustomers />
              </ProtectedRoute>
            } />
            <Route path="/admin/reviews" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminReviews />
              </ProtectedRoute>
            } />
            <Route path="/admin/cadastros/marcas" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminBrands />
              </ProtectedRoute>
            } />
            <Route path="/admin/cadastros/categorias" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminCategories />
              </ProtectedRoute>
            } />
            <Route path="/admin/cadastros/zonas-entrega" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminShippingZones />
              </ProtectedRoute>
            } />
            <Route path="/admin/comunicacao/templates-email" element={
              <ProtectedRoute requiredRoles={['ADMIN']} fallbackPath="/">
                <AdminEmailTemplates />
              </ProtectedRoute>
            } />
            <Route path="/termos" element={<TermsOfUse />} />
            <Route path="/privacidade" element={<PrivacyPolicy />} />
            <Route path="/teste-fipe" element={<FipeTest />} />
            <Route path="/ajuda" element={<HelpCenter />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
      </Router>
      </ScrapPreferenceWrapper>
    </>
  )
}

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppContent />
      </PersistGate>
    </Provider>
  )
}

export default App
